import {
  Component,
  ComponentRef,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewContainerRef
} from '@angular/core';
import {NGXLogger} from 'ngx-logger';
import {ClientConfiguration, ComponentConfiguration} from '@twpub/core/utils';
import {ComponentCategory, SessionParam} from '@twpub/core/enums';
import {ConfigurableComponent, SessionObject} from '@twpub/core/models';
import {ClientConfigurationService, ComponentDeclarationService, SharedStateService} from '@twpub/core/services';
import {ActivatedRoute} from '@angular/router';
import {distinctUntilChanged, filter, map, Subscription} from 'rxjs';
import _ from 'lodash';

@Component({
  template: '',
  standalone: false
})

export abstract class BaseWrapperComponent<T extends ConfigurableComponent> implements OnInit, OnChanges, OnDestroy {
  protected abstract compCategory: ComponentCategory
  protected changesTriggers: SessionParam[] = []
  protected allowMissingConfig = false;
  protected logger: NGXLogger
  protected clientConfig!: ClientConfiguration
  protected sharedStateService: SharedStateService;
  protected componentConfig?: ComponentConfiguration<T>
  protected componentRef?: ComponentRef<T>
  private clientConfigService: ClientConfigurationService
  private componentDeclarationService: ComponentDeclarationService;
  private route: ActivatedRoute;

  visible: boolean = true
  protected subscriptions = new Subscription();

  @Input() configId?: number; // Overrides route data clientConfiguration

  private readonly childName: string;

  constructor(injector: Injector) {
    this.logger = injector.get(NGXLogger);
    this.componentDeclarationService = injector.get(ComponentDeclarationService);
    this.sharedStateService = injector.get(SharedStateService);
    this.clientConfigService = injector.get(ClientConfigurationService);
    this.route = injector.get(ActivatedRoute);
    this.childName = this.constructor.name;
  }

  filterOutTriggerParams = (session: SessionObject) =>
    Object.fromEntries(
      this.changesTriggers
        .filter(param => session?.hasOwnProperty(param))
        .map(trigger => [trigger, _.cloneDeep(session[trigger])])
    ) as Partial<SessionObject>;

  // Store the last changed props to avoid mutation issues
  private lastChangedProps: Partial<SessionObject> = {};

  createPartialWithChanges = (prev: Partial<SessionObject>, curr: Partial<SessionObject>) => {
    const changedProps = Object.fromEntries(
      this.changesTriggers
        .filter(param => !_.isEqual(prev?.[param], curr?.[param]))
        .map(param => [param, curr[param]])
    ) as Partial<SessionObject>;
    if (Object.keys(changedProps).length > 0) {
      // Store changed props without mutating curr
      this.lastChangedProps = changedProps;
      return false; // Not equal, emit the value
    }
    return true; // Equal, don't emit
  };

  getLastChangedProps = () => this.lastChangedProps;

  ngOnInit(): void {
    this.route.data.subscribe({
      next: (data) => {
        this.clientConfig = data.clientConfig
        this.log('init from route data:', {config: this.clientConfig});
        this.loadComponent();
        this.init();
      }
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('configId')) {
      this.configId = changes.configId.currentValue;
      this.clientConfigService.getDefaultConfiguration(this.configId).subscribe((config) => {
        this.clientConfig = config;
        this.log('init from configId:', {config});
        this.loadComponent();
        this.init();
      });
    }
  }

  protected init(): void {
    if (this.changesTriggers.length === 0) {
      this.update();
      return;
    }
    this.subscriptions.add(
      this.sharedStateService.session$
        .pipe(
          map(this.filterOutTriggerParams),
          distinctUntilChanged(this.createPartialWithChanges),
          map(() => this.getLastChangedProps()),
          filter((session) => this.changesTriggers.some((param) => session?.hasOwnProperty(param)))
        ).subscribe((session: Partial<SessionObject>) => {
          this.handleSessionChanges(session);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadComponent() {
    this.getViewContainerRef().clear()
    if (this.clientConfig) {
      this.createComponent();
    } else if (!this.allowMissingConfig) {
      throw new Error('No client configuration for ' + this.constructor.name);
    }
  }

  protected createComponent(): T {
    this.componentConfig = this.getConfiguration(this.compCategory);
    this.componentRef = this.getViewContainerRef().createComponent<T>(this.componentConfig.component);
    return this.componentRef.instance;
  }

  protected getConfiguration(compCategory: ComponentCategory): ComponentConfiguration<any> {
    let config = this.clientConfig.getConfigurationSafe(compCategory);
    if (!config) {
      config = this.componentDeclarationService.createComponentConfigurationForCategory(compCategory, this.clientConfig.scheme);
      if (!config) {
        throw new Error('No component for type ' + compCategory + ' in scheme ' + this.clientConfig.scheme);
      }
    }
    return config;
  }

  protected abstract getViewContainerRef(): ViewContainerRef

  update(updateFn?: (comp: T) => void, updateArgs?: any) {
    if (this.componentRef) {
      const comp = this.componentRef.instance;
      this.log('updates component')
      comp.logger = this.logger;
      comp.config = this.componentConfig;
      comp.compCategory = this.compCategory;
      if (updateFn) {
        updateFn(comp);
      }
      comp.doUpdate?.(updateArgs);
    } else {
      this.log('No componentRef')
    }
  }

  protected handleSessionChanges(session: Partial<SessionObject>): void {
    this.update();
  }

  private log = (msg: string, msgObj?: any) => {
    const message = 'BaseWrapperComponent (' + this.childName + ') ' + msg;
    this.logger.trace(message, msgObj || '');
  }
}
